import { useCallback, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import type { MapareRow } from '@lyra/shared';
import { Button } from '@/components/ui/button';
import { formatNumberRo } from '@/lib/format';
import { ApiClientError } from '@/lib/apiClient';
import { useActiveSession, useStartSession } from '@/lib/walletHooks';
import type { useRetryRows } from './api/reviewHooks';

/**
 * Retry-failed-rows orchestration (Plan 3c-2 Task 6). Ports v1
 * `useClassification.ts`'s `retryErrorRows` chunking (~L566-635,
 * `RETRY_CONCURRENCY = 3`) onto Task 3's `useRetryRows` mutation, and fixes
 * the 403 v1 silently swallowed (spec decision D3): v1's per-row `catch`
 * turned EVERY failure — including "no active instance session" — into a
 * row-level `status: 'error'`, so an operator with no running instance saw
 * every retry silently fail as a classification error with no indication
 * they needed to start an instance. Here, a 403 thrown by `mutateAsync`
 * (`ApiClientError.status === 403`) is NOT swallowed per-row: it stops the
 * whole batch (no further chunks are launched) and surfaces the server's
 * Romanian reason (`POST /classify`'s `'Nu există o instanță activă pentru
 * organizația dvs.'`, from `server/src/routes/classify.ts`) with a
 * „Pornește instanța" CTA, mirroring `features/panou/Instanta.tsx`'s
 * catch-403-set-local-message pattern.
 *
 * Any OTHER error (network blip, a genuinely bad row) is still swallowed
 * per-row exactly like v1 — the row simply stays unpatched (still „Eroare"
 * or classified-but-empty after the run(runId) refetch) and the batch moves
 * on to the next chunk, so one bad row can't block the rest.
 */
const RETRY_CONCURRENCY = 3;

export interface RetryOrchestrator {
  /** Runs the bounded-concurrency retry loop over `candidates` (1 row for a
   *  per-row retry, N rows for "Reîncearcă toate"). No-op while already
   *  running. */
  runRetry: (candidates: MapareRow[]) => Promise<void>;
  isRunning: boolean;
  progress: { done: number; total: number } | null;
  /** Set when a retry attempt hit the "no active instance session" 403 —
   *  the reactive case. Server's Romanian message, verbatim. */
  sessionErrorMessage: string | null;
  /** True once `useActiveSession()` has resolved (not pending/error) — only
   *  then can "no session" be asserted proactively, per spec §6. */
  sessionKnown: boolean;
  hasActiveSession: boolean;
  startSession: () => void;
  isStarting: boolean;
  /** Set when `useStartSession()` itself 403s — a DIFFERENT reason
   *  (insufficient wallet balance), distinct from `sessionErrorMessage`. */
  startErrorMessage: string | null;
}

/**
 * Shared retry state/orchestration, consumed by both the „Erori" header's
 * bulk „Reîncearcă toate" control and every eligible row's individual
 * „Reîncearcă" button (`ResultsTable.tsx`) — a single instance per table so
 * only one retry batch (bulk or single-row) can run at a time, and a 403
 * hit via either path blocks/unblocks both the same way.
 */
export function useRetryOrchestrator(retryMutation: ReturnType<typeof useRetryRows>): RetryOrchestrator {
  const { data: activeSession, isPending: sessionPending, isError: sessionError } = useActiveSession();
  const startSessionMutation = useStartSession();

  const runningRef = useRef(false);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [sessionErrorMessage, setSessionErrorMessage] = useState<string | null>(null);
  const [startErrorMessage, setStartErrorMessage] = useState<string | null>(null);

  const runRetry = useCallback(
    async (candidates: MapareRow[]) => {
      if (candidates.length === 0 || runningRef.current) return;
      runningRef.current = true;
      setIsRunning(true);
      setSessionErrorMessage(null);
      setProgress({ done: 0, total: candidates.length });
      try {
        for (let start = 0; start < candidates.length; start += RETRY_CONCURRENCY) {
          const chunk = candidates.slice(start, start + RETRY_CONCURRENCY);
          let stopMessage: string | null = null;
          // Each row's own try/catch keeps this `Promise.all` from ever
          // rejecting (mirrors v1) — a chunk always runs to completion so
          // `progress` advances by the full chunk size, whether or not
          // individual rows succeeded.
          await Promise.all(
            chunk.map(async (row) => {
              try {
                await retryMutation.mutateAsync(row);
              } catch (err) {
                if (err instanceof ApiClientError && err.status === 403) {
                  stopMessage = err.message;
                }
                // Non-403 errors: swallow — the row stays as-is server-side
                // (still `rowsNeedingRetry`-eligible next time), same as v1.
              }
            })
          );
          setProgress((p) => (p ? { done: Math.min(p.done + chunk.length, p.total), total: p.total } : p));
          if (stopMessage) {
            setSessionErrorMessage(stopMessage);
            break; // D3: stop the batch — don't launch further chunks against a dead session.
          }
        }
      } finally {
        runningRef.current = false;
        setIsRunning(false);
        setProgress(null);
      }
    },
    [retryMutation]
  );

  const startSession = useCallback(() => {
    setStartErrorMessage(null);
    startSessionMutation.mutate(undefined, {
      onSuccess: () => setSessionErrorMessage(null), // let the user re-run retry
      onError: (err) => {
        if (err instanceof ApiClientError && err.status === 403) setStartErrorMessage(err.message);
        // 409 (already active, race) resolves via the query invalidation/refetch — no message needed.
      },
    });
  }, [startSessionMutation]);

  return {
    runRetry,
    isRunning,
    progress,
    sessionErrorMessage,
    sessionKnown: !sessionPending && !sessionError,
    hasActiveSession: Boolean(activeSession),
    startSession,
    isStarting: startSessionMutation.isPending,
    startErrorMessage,
  };
}

/**
 * True once retry controls should refuse to fire — either mid-batch, or
 * because we already know (proactively or from a just-hit 403) there's no
 * active instance session to retry against. Shared by the bulk bar and
 * every per-row button so they never disagree about whether retrying is
 * currently possible.
 */
export function isRetryBlocked(o: RetryOrchestrator): boolean {
  return o.isRunning || Boolean(o.sessionErrorMessage) || (o.sessionKnown && !o.hasActiveSession);
}

/** Title/tooltip explaining why a retry control is disabled, or `undefined` when it isn't. */
export function retryBlockedReason(o: RetryOrchestrator): string | undefined {
  if (o.isRunning) return 'Se reîncearcă…';
  if (o.sessionErrorMessage) return o.sessionErrorMessage;
  if (o.sessionKnown && !o.hasActiveSession) return 'Nicio instanță activă — porniți instanța pentru a reîncerca.';
  return undefined;
}

/**
 * Bulk „Reîncearcă toate (N)" control + progress + 403 CTA, meant for the
 * „Erori" group header. `candidates` is `rowsNeedingRetry(rows)` over the
 * WHOLE table (Task 1) — may exceed the „Erori" section's own row count,
 * since classified-but-empty rows (no error, but no usable classification
 * either) are also retry candidates even though they render under
 * „Rezolvate", not „Erori" (per `groupResults`).
 */
export function RetryBar({ candidates, orchestrator }: { candidates: MapareRow[]; orchestrator: RetryOrchestrator }) {
  const { progress, sessionErrorMessage, startErrorMessage, isStarting, startSession, runRetry } = orchestrator;
  if (candidates.length === 0) return null;

  const blocked = isRetryBlocked(orchestrator);
  const showStartCta = sessionErrorMessage != null || (orchestrator.sessionKnown && !orchestrator.hasActiveSession);

  return (
    <div className="ml-auto flex flex-wrap items-center gap-2">
      {progress && (
        <span className="text-[12.5px] font-normal normal-case tabular-nums text-ink-3">
          Se reîncearcă… {progress.done}/{progress.total}
        </span>
      )}
      {sessionErrorMessage && (
        <span className="text-[12.5px] font-normal normal-case text-bad">{sessionErrorMessage}</span>
      )}
      {startErrorMessage && <span className="text-[12.5px] font-normal normal-case text-bad">{startErrorMessage}</span>}
      {showStartCta && (
        <Button type="button" size="sm" onClick={startSession} disabled={isStarting}>
          {isStarting ? 'Se pornește…' : 'Pornește instanța'}
        </Button>
      )}
      <button
        type="button"
        onClick={() => runRetry(candidates)}
        disabled={blocked}
        title={retryBlockedReason(orchestrator)}
        className="text-[12.5px] font-medium normal-case text-teal hover:underline disabled:cursor-not-allowed disabled:text-ink-3 disabled:no-underline"
      >
        Reîncearcă toate ({formatNumberRo(candidates.length)})
      </button>
    </div>
  );
}

/** Per-row „Reîncearcă" icon button — rendered next to the expand toggle for
 *  every row `rowsNeedingRetry` selects, wherever it renders (Erori or, for
 *  classified-but-empty rows, Rezolvate). Shares `orchestrator`'s
 *  running/blocked state with the bulk control. */
export function RetryRowButton({ row, orchestrator }: { row: MapareRow; orchestrator: RetryOrchestrator }) {
  const blocked = isRetryBlocked(orchestrator);
  return (
    <button
      type="button"
      onClick={() => orchestrator.runRetry([row])}
      disabled={blocked}
      aria-label={`Reîncearcă „${row.name}"`}
      title={retryBlockedReason(orchestrator) ?? 'Reîncearcă'}
      className="flex size-6 shrink-0 items-center justify-center rounded text-ink-3 hover:text-teal disabled:opacity-40 disabled:hover:text-ink-3"
    >
      <RefreshCw className="size-3.5" />
    </button>
  );
}
